// src/whatsappChat/components/NewChatModal.jsx
import React, { useState } from 'react';
import { findWaConversation, useWaSendMessage } from '../api/hooks';
import { sendErrorMessage } from '../api/client';
import { digitsOnly, formatPhone, numberLabel } from '../api/content';
import { showToast } from '../../components/toast.jsx';
import { Field, Modal } from './Modal';
import { TemplateForm } from './TemplateForm';

/**
 * Start a chat: phone + "send from" number, then a template (the first
 * message to a customer normally has to be one). If a chat already exists
 * for that phone on that number it is opened instead.
 */
export function NewChatModal({ numbers, initialPhone = '', onClose, onOpenChat }) {
  const sendMessage = useWaSendMessage();
  const [phone, setPhone] = useState(initialPhone);
  const [numberId, setNumberId] = useState(numbers[0]?.number_id || '');
  const [step, setStep] = useState('address'); // 'address' | 'template'
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState('');

  const digits = digitsOnly(phone);
  const busy = checking || sendMessage.isPending;

  const next = async (e) => {
    e.preventDefault();
    if (busy) return;
    if (digits.length < 10 || digits.length > 15) {
      setError('Enter a 10-digit mobile number, or the full number with country code.');
      return;
    }
    if (!numberId) {
      setError('Choose the number to send from.');
      return;
    }
    setError('');
    setChecking(true);
    try {
      const existing = await findWaConversation(digits, numberId);
      if (existing) {
        showToast.success('A chat with this number already exists.');
        onOpenChat(existing.id);
        return;
      }
    } catch {
      // The lookup is a convenience; sending is still checked by the server.
    } finally {
      setChecking(false);
    }
    setStep('template');
  };

  const send = async (fields) => {
    try {
      const sent = await sendMessage.mutateAsync({ phone: digits, number_id: numberId, ...fields });
      if (sent?.conversation_id) onOpenChat(sent.conversation_id);
      else onClose();
    } catch (err) {
      showToast.error(sendErrorMessage(err));
    }
  };

  const numberName = numbers.find((n) => n.number_id === numberId)?.name;

  return (
    <Modal title="New chat" wide={step === 'template'} locked={busy} onClose={onClose}>
      {step === 'address' ? (
        <form className="wa-form" onSubmit={next}>
          <Field
            label="Customer mobile number"
            hint="A 10-digit number is treated as an Indian number."
            error={error}
          >
            <input
              className="wa-input"
              type="tel"
              inputMode="numeric"
              autoFocus
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="9876543210"
            />
          </Field>

          <Field label="Send from">
            <select className="wa-input" value={numberId} onChange={(e) => setNumberId(e.target.value)}>
              {numbers.length === 0 && <option value="">No numbers configured</option>}
              {numbers.map((n) => (
                <option key={n.number_id} value={n.number_id}>
                  {numberLabel(n)}
                </option>
              ))}
            </select>
          </Field>

          <div className="wa-modal-actions">
            <button type="button" className="wa-btn wa-btn-secondary" onClick={onClose} disabled={busy}>
              Cancel
            </button>
            <button type="submit" className="wa-btn wa-btn-primary" disabled={busy || numbers.length === 0}>
              {checking ? 'Checking…' : 'Next'}
            </button>
          </div>
        </form>
      ) : (
        <>
          <p className="wa-modal-note">
            To <strong>{digits.length === 10 ? formatPhone(`91${digits}`) : formatPhone(digits)}</strong>
            {numberName ? <> from <strong>{numberName}</strong></> : null}
          </p>
          <TemplateForm
            numberId={numberId}
            sending={busy}
            onSend={send}
            onCancel={() => setStep('address')}
            submitLabel="Start chat"
          />
        </>
      )}
    </Modal>
  );
}
